import React, { useMemo } from 'react';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { IWiseTableColumn, IWiseTableAction } from 'components/wise/types';
import { COLUMN_RENDERERS, defaultRenderer } from 'components/wise/WiseTable/utils/columnRenderers';
import {
  getEffectiveColumnWidth,
  MIN_COLUMN_WIDTH_PX
} from 'components/wise/WiseTable/constants';
import { MainAction, AdditionalAction, ColumnAction } from 'plugins/components/controls';
import { useAppTheme } from 'theme/useAppTheme';

interface IUseWiseTableColumnsParams {
  columns: IWiseTableColumn[];
  mainActions: IWiseTableAction[];
  additionalActions: IWiseTableAction[];
  columnActions: IWiseTableAction[];
  refetchTableData: () => void;
  requestParams?: object;
}

/**
 * Hook do mapowania kolumn
 */
export const useWiseTableColumns = ({
  columns,
  mainActions,
  additionalActions,
  columnActions,
  refetchTableData,
  requestParams
}: IUseWiseTableColumnsParams): MRT_ColumnDef<any, any>[] => {
  const theme = useAppTheme();
  const mrtColumns = useMemo<MRT_ColumnDef<any, any>[]>(() => {
    const dataColumns: MRT_ColumnDef<any, any>[] = columns.map((column) => {
      const matchingColumnAction = columnActions.find(
        (action) => action.column_field_symbol === column.field_symbol
      );

      const muiAlign =
        typeof column.align === 'string'
          ? { align: column.align as 'left' | 'center' | 'right' | 'justify' | 'inherit' }
          : {};

      const columnDef: MRT_ColumnDef<any, any> = {
        id: column.field_symbol,
        accessorKey: column.field_symbol,
        header: column.label,
        enableSorting: true,
        muiTableHeadCellProps: {
          ...muiAlign,
          title: column.label,
          sx: {
            fontWeight: 'bold',
            borderBottom: `1px solid ${theme.palette.app.tableBorder}`
          }
        },
        muiTableBodyCellProps: {
          ...muiAlign
        },
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          const item = row.original;

          if (matchingColumnAction) {
            return (
              <ColumnAction action={matchingColumnAction} item={item} refetch={refetchTableData} />
            );
          }

          const renderer = COLUMN_RENDERERS[column.type] || defaultRenderer;

          return renderer(item, column.field_symbol, undefined, refetchTableData, column);
        }
      };

      // Szerokość kolumny – z constants (MIN_COLUMN_WIDTH_PX), overflow-x na wrapperze
      const colSize = getEffectiveColumnWidth(column.size);
      columnDef.size = colSize;
      if (column.minSize !== undefined) {
        columnDef.minSize = Math.max(column.minSize, MIN_COLUMN_WIDTH_PX);
      }
      if (column.maxSize !== undefined) {
        columnDef.maxSize = column.maxSize;
      }

      return columnDef;
    });

    // Akcje kontekstowe są już przefiltrowane w useWiseTableActions

    // Dodanie kolumny akcji jeśli są akcje kontekstowe
    if (mainActions.length > 0 || additionalActions.length > 0) {
      const actionsColumn: MRT_ColumnDef<any, any> = {
        id: 'actions',
        header: 'Akcje',
        enableSorting: false,
        size: 80,
        muiTableHeadCellProps: {
          sx: {
            fontWeight: 'bold',
            borderBottom: `1px solid ${theme.palette.app.tableBorder}`
          }
        },
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          const item = row.original;
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {mainActions.map((action, i) => (
                <MainAction key={i} action={action as IWiseTableAction} refetch={refetchTableData} item={item} />
              ))}
              {additionalActions.length > 0 && (
                <AdditionalAction
                  additionalActions={additionalActions as IWiseTableAction[]}
                  item={item}
                  refetchTableData={refetchTableData}
                  requestParams={requestParams}
                />
              )}
            </div>
          );
        }
      };

      return [...dataColumns, actionsColumn];
    }

    return dataColumns;
  }, [columns, mainActions, additionalActions, columnActions, refetchTableData, requestParams]);

  return mrtColumns;
};
